import { useGetProvincesConfigs } from '@src/api/config-api'
import { useGetCreditApplicationById } from '@src/api/credit-api'
import { Loader } from '@src/components'
import { scrollToTop } from '@src/services/utils'
import { ESessionStorageKeys } from '@src/types/constants'
import { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import AddressForm from './components/AddressForm'
import ContactInformationForm from './components/ContactInformationForm'
import EmploymentForm from './components/EmploymentForm'
import PersonalInformationForm from './components/PersonalInformationForm'
import { Applicant, PersonalInformationInput, Prequalification } from './components/PrequalificationSchema'
import ReviewCoapplicant from './components/ReviewCoapplicant'
import StepperFormFooter from './components/StepperFormFooter'
import StepProgressionBar from './components/StepProgressionBar'
import {
  buildDefaultCoapplicant,
  useConvertedLoanPurposeId,
  useSessionStorage,
  useStepListForApplicant,
} from './submit-app-hooks'

const AddCoapplicantPage = () => {
  const urlParams = useParams()
  const id = urlParams.id!
  const currentStep = urlParams.currentStep!
  const navigate = useNavigate()

  useEffect(() => {
    const savedcoApplicantInfo = sessionStorage.getItem(ESessionStorageKeys.COAPPLICANT_INFO)
    if (!savedcoApplicantInfo) navigate(`/creditapplication/${id}/addcoapplicant/personal-information`)
  }, [id, navigate])

  const { t } = useTranslation()

  const steps = useStepListForApplicant()

  const cls = `form-section step-${currentStep}-active`

  const [creditApp, isLoadingCreditApp] = useGetCreditApplicationById(id, false)
  const [provinceConfigs, isLoadingConfig] = useGetProvincesConfigs()
  const [prequalInfo, setPrequalInfo] = useSessionStorage<Prequalification>(
    ESessionStorageKeys.COAPPLICANT_INFO,
    buildDefaultCoapplicant(),
  )
  const [disableNext, setDisableNext] = useState(false)

  const gotoStep = useCallback((stepPath: string) => {
    scrollToTop()
    navigate(`/creditapplication/${id}/addcoapplicant/${stepPath}`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const nextStepAfter = useCallback(() => {
    const nextStepIdx = steps.indexOf(currentStep.toLowerCase()) + 1
    if (nextStepIdx < steps.length) {
      gotoStep(steps[nextStepIdx])
    }
  }, [currentStep, gotoStep, steps])

  const handlePrequalificationUpdated = ({ applicant, ...data }: PersonalInformationInput) => {
    const update = {
      ...prequalInfo,
      ...data,
      applicant: { ...prequalInfo.applicant, ...applicant },
    } as Prequalification
    setPrequalInfo(update)
    nextStepAfter()
  }
  const handleApplicantUpdated = (data: Partial<Applicant>) => {
    const update = { ...prequalInfo, applicant: { ...prequalInfo.applicant, ...data } }
    setPrequalInfo(update)
    nextStepAfter()
  }

  useLayoutEffect(scrollToTop, [])

  const convertedLoanPurposeId = useConvertedLoanPurposeId(prequalInfo.loanPurposeId)

  if (isLoadingCreditApp || isLoadingConfig)
    return <Loader title={t('common.withYouShortly')} description={t('common.pleaseWait')} />
  return (
    <main id="add-coapplicant-form" className={cls}>
      <StepProgressionBar steps={steps} currentStep={currentStep} isCoapplicant />
      {currentStep === 'personal-information' && (
        <PersonalInformationForm
          isCoapplicant
          prequalificationData={prequalInfo}
          onPrequalificationUpdated={handlePrequalificationUpdated}
          loanPurposeId={convertedLoanPurposeId}
        />
      )}

      {currentStep === 'contact' && (
        <ContactInformationForm
          onApplicantUpdated={handleApplicantUpdated}
          applicantData={prequalInfo.applicant}
          prohibedPhone={creditApp?.applicant.cellPhone}
        />
      )}

      {currentStep === 'address' && (
        <AddressForm
          applicantData={prequalInfo.applicant}
          onApplicantUpdated={handleApplicantUpdated}
          applicantName={prequalInfo.applicant.firstName}
          setDisableNext={setDisableNext}
        />
      )}

      {currentStep === 'employment' && (
        <EmploymentForm applicantData={prequalInfo.applicant} onApplicantUpdated={handleApplicantUpdated} />
      )}

      {currentStep === 'review' && (
        <ReviewCoapplicant
          gotoStep={gotoStep}
          prequalification={prequalInfo}
          creditApplicationId={id}
          provinceConfigs={provinceConfigs}
        />
      )}

      {currentStep !== 'review' && (
        <StepperFormFooter
          currentStepIndex={steps.indexOf(currentStep) + 1}
          stepLength={steps.length}
          disableBack={false}
          disableNext={disableNext}
        />
      )}
    </main>
  )
}

export default AddCoapplicantPage
